import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapTemplate } from '@templates';
import {
  AssetTypeConfig,
  emptyServiceLocationDetails,
  ErrorMessages,
  GulliesAsset,
} from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  errorFormatter,
  issueLocationValidation,
  stringFieldSchema,
} from '@utils';
import { ZodError } from 'zod';
import { LatLngTuple } from 'leaflet';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { useDrainsStore } from '../../../../hooks/useDrainsStore';
import { RoutesList } from '../../../../routes/Routes';

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const errorPath = `issue-Location`;
  const pageTitle = 'Where is the drain issue located?';

  const {
    update,
    data: { serviceLocationDetails, assetDetails },
  } = useDrainsStore();

  const subText =
    'Select a drain on the map by searching or zooming in. Each location is marked by a blue circle.';

  const { latitude, longitude } = serviceLocationDetails;

  const defaultLocation =
    latitude && longitude ? ([latitude, longitude] as LatLngTuple) : undefined;

  const handleOnPreviousClick = () => {
    navigate(RoutesList.UPLOAD_PHOTOS);
  };

  const handleOnNextClick = () => {
    setErrorMessages([]);

    try {
      issueLocationValidation.parse({
        locationIssue: serviceLocationDetails.UPRN ?? '',
      });
      stringFieldSchema('assetID', 'Select a blue circle on the map').parse({
        assetID: assetDetails?.central_asset_id || '',
      });
      navigate(RoutesList.LOCATION_ADDITIONAL_INFORMATION);
    } catch (error) {
      const { errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages([
        { path: errorPath, message: errorMessages[0].message },
      ]);
    }
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = async (address: addressResponseType) => {
    setErrorMessages([]);
    update('assetDetails', null);
    if (Object.keys(address).length === 0) {
      // Clears address and asset details when user clears address from search bar
      update('serviceLocationDetails', emptyServiceLocationDetails);
      return;
    } else {
      update(
        'serviceLocationDetails',
        convertAddressToServiceLocation(address),
      );
      if (
        address.asset &&
        (address.asset as GulliesAsset)?.feature_type_code === 'HWGY'
      ) {
        update('assetDetails', address.asset as GulliesAsset);
      }
    }
  };

  const drainsAsset: AssetTypeConfig = {
    asset: 'HighwaysGullies',
    icon: {
      shape: 'circle',
      size: 56,
    },
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `${geojson.properties.site_name}, Drain:${geojson.properties.central_asset_id}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  const assetsToDisplay = [drainsAsset];

  const minZoomToDisplayAssets = {
    targetAssetType: drainsAsset.asset,
    minZoom: 18,
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      assetType={assetsToDisplay}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      defaultLocation={defaultLocation}
      onChangeAddress={handleAddressChange}
      selectedAddress={convertServiceLocationToAddress(serviceLocationDetails)}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      minZoomToDisplayAssets={minZoomToDisplayAssets}
    />
  );
};
